import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { 
  Check, X, Crown, Zap, Building2, Star, ArrowRight, 
  Users, Calendar, FileText, Shield, Headphones, TrendingUp,
  Award, Eye, Target, Sparkles, Clock, Globe, Menu
} from 'lucide-react';
import Footer from '@/components/Footer';
import ScrollingLogo from '@/components/ScrollingLogo';

// Client Subscription Plans
const clientPlans = [
  {
    id: 'client_basic',
    name: 'Starter',
    description: 'Perfect for small teams getting started',
    price: 0,
    period: 'forever',
    popular: false,
    features: [
      { text: 'Up to 5 RFP requests/month', included: true },
      { text: 'Basic venue search', included: true },
      { text: 'Email support', included: true },
      { text: 'Standard response time (48hrs)', included: true },
      { text: 'Bulk RFP creation', included: false },
      { text: 'Priority vendor matching', included: false },
      { text: 'Dedicated account manager', included: false },
      { text: 'Custom reporting', included: false },
    ],
    cta: 'Get Started Free',
    color: 'slate',
  },
  {
    id: 'client_professional',
    name: 'Professional',
    description: 'For growing companies with regular events',
    price: 9999,
    period: 'month',
    popular: true,
    features: [
      { text: 'Unlimited RFP requests', included: true },
      { text: 'Advanced venue search & filters', included: true },
      { text: 'Priority email & chat support', included: true },
      { text: 'Fast response time (12hrs)', included: true },
      { text: 'Bulk RFP creation', included: true },
      { text: 'Priority vendor matching', included: true },
      { text: 'Dedicated account manager', included: false },
      { text: 'Custom reporting', included: false },
    ],
    cta: 'Start Professional',
    color: 'gold',
  },
  {
    id: 'client_enterprise',
    name: 'Enterprise',
    description: 'For large organizations with complex needs',
    price: 29999,
    period: 'month',
    popular: false,
    features: [
      { text: 'Unlimited everything', included: true },
      { text: 'White-glove venue sourcing', included: true },
      { text: '24/7 priority support', included: true },
      { text: 'Instant response time', included: true },
      { text: 'Bulk RFP with AI suggestions', included: true },
      { text: 'Exclusive vendor network', included: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'Custom reporting & analytics', included: true },
    ],
    cta: 'Contact Sales',
    color: 'navy',
  },
];

// Vendor Listing Plans
const vendorPlans = [
  {
    id: 'vendor_standard',
    name: 'Standard',
    description: 'Basic listing for new vendors',
    price: 0,
    period: 'forever',
    popular: false,
    features: [
      { text: 'Basic property listing', included: true },
      { text: 'Up to 5 photos per property', included: true },
      { text: 'Receive RFP inquiries', included: true },
      { text: 'Standard search placement', included: true },
      { text: 'Featured badge', included: false },
      { text: 'Priority in search results', included: false },
      { text: 'Analytics dashboard', included: false },
      { text: 'Verified badge', included: false },
    ],
    cta: 'List for Free',
    color: 'slate',
  },
  {
    id: 'vendor_featured',
    name: 'Featured',
    description: 'Stand out from the competition',
    price: 4999,
    period: 'month',
    popular: true,
    features: [
      { text: 'Enhanced property listing', included: true },
      { text: 'Up to 20 photos + video tour', included: true },
      { text: 'Priority RFP notifications', included: true },
      { text: 'Top search placement', included: true },
      { text: 'Featured badge on listings', included: true },
      { text: 'Priority in search results', included: true },
      { text: 'Basic analytics dashboard', included: true },
      { text: 'Verified badge', included: false },
    ],
    cta: 'Go Featured',
    color: 'gold',
  },
  {
    id: 'vendor_premium',
    name: 'Premium',
    description: 'Maximum visibility & leads',
    price: 14999,
    period: 'month',
    popular: false,
    features: [
      { text: 'Premium property showcase', included: true },
      { text: 'Unlimited photos + 360° tours', included: true },
      { text: 'Exclusive lead generation', included: true },
      { text: 'Homepage featured carousel', included: true },
      { text: 'Premium + Featured badges', included: true },
      { text: '#1 search placement', included: true },
      { text: 'Advanced analytics + insights', included: true },
      { text: 'Verified badge + trust seal', included: true },
    ],
    cta: 'Go Premium',
    color: 'navy',
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('clients');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast.info('Please login to subscribe');
      navigate('/');
      return;
    }

    if (plan.price === 0) {
      toast.success(`You're on the ${plan.name} plan!`);
      return;
    }

    try {
      // Create Razorpay subscription order
      const response = await api.createRazorpayOrder({
        amount: billingPeriod === 'yearly' ? plan.price * 10 : plan.price, // 2 months free on yearly
        payment_type: 'subscription',
        plan_id: plan.id,
        billing_period: billingPeriod,
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: response.data.amount,
        currency: response.data.currency,
        name: 'My Corporate Hub',
        description: `${plan.name} Plan - ${billingPeriod === 'yearly' ? 'Annual' : 'Monthly'} Subscription`,
        image: 'https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png',
        order_id: response.data.order_id,
        handler: async function (razorpayResponse) {
          try {
            await api.verifyRazorpayPayment({
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
              plan_id: plan.id,
              billing_period: billingPeriod,
            });
            toast.success(`Successfully subscribed to ${plan.name} plan!`);
            navigate('/dashboard/' + (user.role === 'vendor' ? 'vendor' : 'client'));
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name || '',
          email: user.email || '',
        },
        theme: {
          color: '#1e3a5f',
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  const PlanCard = ({ plan, type }) => (
    <Card className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
      plan.popular ? 'border-2 border-gold shadow-lg scale-105' : 'border border-gray-200'
    }`}>
      {plan.popular && (
        <div className="absolute top-0 right-0 bg-gold text-navy px-4 py-1 text-xs font-bold rounded-bl-lg">
          MOST POPULAR
        </div>
      )}
      <CardHeader className={`pb-4 ${plan.popular ? 'bg-gradient-to-r from-gold/10 to-gold/5' : ''}`}>
        <div className="flex items-center gap-2 mb-2">
          {plan.color === 'gold' && <Crown className="w-5 h-5 text-gold" />}
          {plan.color === 'navy' && <Sparkles className="w-5 h-5 text-navy" />}
          {plan.color === 'slate' && <Zap className="w-5 h-5 text-slate-600" />}
          <CardTitle className="text-xl font-bold text-navy">{plan.name}</CardTitle>
        </div>
        <CardDescription className="text-slate-700">{plan.description}</CardDescription>
        <div className="mt-4">
          <span className="text-4xl font-bold text-navy">
            {plan.price === 0 ? 'Free' : `₹${(billingPeriod === 'yearly' ? plan.price * 10 : plan.price).toLocaleString()}`}
          </span>
          {plan.price > 0 && (
            <span className="text-slate-700 ml-1">
              /{billingPeriod === 'yearly' ? 'year' : 'month'}
            </span>
          )}
          {billingPeriod === 'yearly' && plan.price > 0 && (
            <Badge className="ml-2 bg-green-100 text-green-800">Save 17%</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <ul className="space-y-3 mb-6">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-3">
              {feature.included ? (
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
              )}
              <span className={feature.included ? 'text-slate-800' : 'text-slate-500'}>
                {feature.text}
              </span>
            </li>
          ))}
        </ul>
        <Button
          onClick={() => handleSubscribe(plan)}
          className={`w-full font-semibold ${
            plan.popular 
              ? 'bg-gold hover:bg-gold-light text-navy' 
              : plan.color === 'navy' 
                ? 'bg-navy hover:bg-navy-700 text-white'
                : 'bg-slate-200 hover:bg-slate-300 text-navy'
          }`}
        >
          {plan.cta}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
          : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 relative">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
              <span className="font-heading text-2xl text-navy font-bold tracking-tight hidden md:block">My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
            </Link>
            
            {/* Centered Menus */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <Link to="/services" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Services</Link>
              <Link to="/properties" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Properties</Link>
              <Link to="/gifts" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Gifting</Link>
              <Link to="/event-permissions" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Event Permits</Link>
              <Link to="/trainers" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Trainers</Link>
              <Link to="/pricing" className="text-gold font-semibold text-sm uppercase tracking-wide transition-colors">Pricing</Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
              </button>
              
              {user ? (
                <>
                  <Link to={`/dashboard/${user.role}`} className="hidden sm:block">
                    <Button variant="ghost" className="text-navy font-medium">Dashboard</Button>
                  </Link>
                  <Button onClick={() => { navigate('/'); }} variant="outline" className="border-navy text-navy hidden sm:flex">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/?login=true" className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Sign In</Button>
                  </Link>
                  <Link to="/bulk-rfp/new">
                    <Button className="bg-gold hover:bg-gold-light text-navy font-bold px-4 sm:px-6 shadow-md text-sm sm:text-base">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2">
              <Link to="/services" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/properties" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Properties</Link>
              <Link to="/gifts" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Gifting</Link>
              <Link to="/event-permissions" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Event Permits</Link>
              <Link to="/trainers" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Trainers</Link>
              <Link to="/pricing" className="block py-3 px-4 text-gold bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <div className="pt-3 border-t border-gray-200 mt-3">
                {user ? (
                  <>
                    <Link to={`/dashboard/${user.role}`} className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                    <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="block w-full text-left py-3 px-4 text-red-600 hover:bg-red-50 rounded-lg font-medium mt-2">Logout</button>
                  </>
                ) : (
                  <Link to="/?login=true" className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1920&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/90 to-navy-800/90" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center bg-gold/10 text-gold border border-gold/30 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
            <Sparkles className="w-4 h-4 mr-2" /> Simple, Transparent Pricing
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Choose the Perfect Plan for Your Business
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Whether you're a corporate client looking for venues or a vendor wanting to showcase your property, we have the right plan for you.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white/10 rounded-full p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                billingPeriod === 'monthly' 
                  ? 'bg-white text-navy shadow-md' 
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full font-semibold transition-all flex items-center gap-2 ${
                billingPeriod === 'yearly' 
                  ? 'bg-white text-navy shadow-md' 
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Yearly
              <Badge className="bg-green-500 text-white text-xs">Save 17%</Badge>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="bg-white border-2 border-navy/20 p-1.5 rounded-full h-auto">
                <TabsTrigger 
                  value="clients" 
                  className="px-8 py-3 font-semibold rounded-full data-[state=active]:bg-navy data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  <Users className="w-4 h-4 mr-2" />
                  For Corporates
                </TabsTrigger>
                <TabsTrigger 
                  value="vendors"
                  className="px-8 py-3 font-semibold rounded-full data-[state=active]:bg-navy data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  For Vendors
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="clients">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                {clientPlans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} type="client" />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="vendors">
              <div className="grid md:grid-cols-3 gap-8 items-start">
                {vendorPlans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} type="vendor" />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-navy text-center mb-12">
            Why Choose My Corporate Hub?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm text-center">
              <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-bold text-navy mb-2">Pan-India Network</h3>
              <p className="text-slate-700 text-sm">Access to 500+ verified venues across 50+ cities in India</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm text-center">
              <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-bold text-navy mb-2">Secure Payments</h3>
              <p className="text-slate-900 text-sm font-medium">100% secure transactions powered by Razorpay</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm text-center">
              <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Headphones className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-bold text-navy mb-2">Dedicated Support</h3>
              <p className="text-slate-700 text-sm">Expert event planners available to assist you</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-navy text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: 'Can I upgrade or downgrade my plan anytime?',
                a: 'Yes, you can change your plan at any time. Upgrades are effective immediately, and downgrades take effect at the next billing cycle.'
              },
              {
                q: 'Is there a free trial for paid plans?',
                a: 'We offer a 14-day free trial for Professional and Featured plans. No credit card required to start.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit/debit cards, UPI, net banking, and wallets through our secure Razorpay payment gateway.'
              },
              {
                q: 'Can I cancel my subscription?',
                a: 'Yes, you can cancel anytime. Your access will continue until the end of your current billing period.'
              },
            ].map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="font-bold text-navy mb-2">{faq.q}</h3>
                <p className="text-slate-700">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-slate-300 mb-8">
            Join thousands of businesses already using My Corporate Hub
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/">
              <Button className="bg-gold hover:bg-gold-light text-navy font-semibold px-8 py-6 text-lg">
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Payment Logos */}
      <section className="py-8 bg-slate-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-slate-900 font-medium mb-4">Secure Payments Powered By</p>
          <div className="flex items-center justify-center gap-6">
            <img src="https://cdn.razorpay.com/logo.svg" alt="Razorpay" className="h-6" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-5" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-8" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Fixed Rotating Logo - Bottom Left */}
      <ScrollingLogo />
    </div>
  );
}
